import { useCallback, useRef, useState } from "react";

/**
 * Shared state machine for a tile's on-demand tooltip object-table fetch:
 * loading/error, single-flight-with-latest-wins, and loaded-key dedupe so
 * re-opening the same tooltip (same cache key) doesn't refetch. The actual
 * network call and its param shape stay 100% caller-defined — different
 * tiles hit different endpoints with different request shapes for
 * conceptually the same "attribute rows for these objects" data.
 *
 * Usage: call `request(key, fetchFn)` from a click/hover handler, where
 * `key` is a string built the same way as the tile's existing cache key
 * (e.g. `[activeFile, bucketStart, bucketEnd, attrs.join(" ")].join("|")`)
 * and `fetchFn` is `() => Promise<{ rows, total_count }>`.
 */
export function useObjectTableFetch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadedKeyRef = useRef(null);
  const inFlightKeyRef = useRef(null);
  const pendingRef = useRef(null);

  const runFetch = useCallback(async (key, fetchFn) => {
    inFlightKeyRef.current = key;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      if (inFlightKeyRef.current !== key) return; // superseded by a newer request
      setData(result);
      loadedKeyRef.current = key;
    } catch (err) {
      if (inFlightKeyRef.current !== key) return;
      setError(err?.message || "Failed to load");
    } finally {
      if (inFlightKeyRef.current === key) {
        setLoading(false);
        inFlightKeyRef.current = null;
      }
      const pending = pendingRef.current;
      if (pending) {
        pendingRef.current = null;
        runFetch(pending.key, pending.fetchFn);
      }
    }
  }, []);

  const request = useCallback(
    (key, fetchFn) => {
      if (loadedKeyRef.current === key && !error) return;
      if (inFlightKeyRef.current) {
        pendingRef.current = { key, fetchFn }; // latest queued request wins, not FIFO
        return;
      }
      runFetch(key, fetchFn);
    },
    [runFetch, error]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    loadedKeyRef.current = null;
    inFlightKeyRef.current = null;
    pendingRef.current = null;
  }, []);

  return { data, loading, error, request, reset };
}
